import { Box, Divider } from "@mui/material"
import type { FC } from "react"
import { Fragment } from "react"
import type { FieldArrayWithId } from "react-hook-form"
import { useFormContext } from "react-hook-form"

import { LocationRowFields } from "./LocationRowFields"
import { StepSectionHeader } from "./StepSectionHeader"

import { AddDataRow } from "@/components/common/AddDataRow"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { AerosTextField } from "@/components/UiKit"
import type { Product } from "@/graphql/codegen/graphql"
import useAssociateProductColumns from "@/screens/Products/hooks/useAssociateProductColumns"
import type { AssociateProductFormValues } from "@/screens/Products/schemas/associateProductForm"
import type { Option } from "@/types/form-fields"

interface AssociateProductLocationsStepProps {
  product: Product
  fields: FieldArrayWithId<AssociateProductFormValues, "locations">[]
  locationOptions: Option<string>[]
  onAddLocation: () => void
  onRemoveLocation: (index: number) => void
}

export const AssociateProductLocationsStep: FC<AssociateProductLocationsStepProps> = ({
  product,
  fields,
  locationOptions,
  onAddLocation,
  onRemoveLocation,
}) => {
  const { watch } = useFormContext<AssociateProductFormValues>()
  const customerCode = watch("customerCode")
  const customerDescription = watch("customerDescription")
  const productColumns = useAssociateProductColumns({ showRadioButton: false })

  return (
    <Box className='flex flex-col gap-10'>
      <Box className='flex flex-col'>
        <StepSectionHeader title='1. Selected Product' complete />
        <AerosBaseTable
          features={{
            tanstackOptions: {
              data: [product],
              columns: productColumns,
              getRowId: (row) => row.productId.toString(),
            },
          }}
        />
      </Box>

      <Box className='flex flex-col'>
        <StepSectionHeader title="2. Customer's Default Product Info" complete />
        <Box className='grid grid-cols-2 gap-4'>
          <AerosTextField
            label='Customer Code'
            value={customerCode.join(", ")}
            disabled
            size='small'
            data-testid='associate-product-customer-code-readonly'
          />
          <AerosTextField
            label='Customer Description'
            value={customerDescription}
            disabled
            size='small'
            data-testid='associate-product-customer-description-readonly'
          />
        </Box>
      </Box>

      <Box className='flex flex-col'>
        <StepSectionHeader title='3. Associate Location(s)' />

        {fields.map((field, index) => (
          <Fragment key={field.id}>
            {index > 0 && <Divider className='my-5' />}
            <LocationRowFields
              index={index}
              locationOptions={locationOptions}
              customerCodes={customerCode}
              onRemove={onRemoveLocation}
            />
          </Fragment>
        ))}

        <Box className='mt-6'>
          <AddDataRow
            label={fields.length === 0 ? "Associate Location" : "Associate Another Location"}
            onClick={onAddLocation}
            data-testid='associate-location-button'
          />
        </Box>
      </Box>
    </Box>
  )
}
